// src/components/FlatRow.jsx
import React, { useRef, useState } from "react";
import { Button, Input, Popconfirm, Space, Tooltip, Typography } from "antd";
import { DeleteOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";

const { Text } = Typography;
const TOUCH_HOLD_MS = 450;

/** Инлайновый редактируемый текст */
function InlineEditableText({ text, onRename, onEditingChange }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(text || "");
  const pressTimer = useRef(null);

  const startEdit = () => {
    setEditing(true);
    onEditingChange?.(true);
    setValue(text || "");
  };
  const clearTimer = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };
  const onTouchStart = () => {
    if (pressTimer.current) return;
    pressTimer.current = setTimeout(() => {
      startEdit();
      clearTimer();
    }, TOUCH_HOLD_MS);
  };
  const save = async () => {
    const ok = await onRename(value);
    if (ok !== false) {
      setEditing(false);
      onEditingChange?.(false);
    }
  };

  if (editing) {
    return (
      <Space.Compact style={{ width: "100%" }}>
        <Input
          autoFocus
          size="middle"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onPressEnter={save}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setEditing(false);
              onEditingChange?.(false);
            }
          }}
        />
        <Button type="primary" size="middle" icon={<CheckOutlined />} onClick={save} />
        <Button
          size="middle"
          icon={<CloseOutlined />}
          onClick={() => {
            setEditing(false);
            onEditingChange?.(false);
          }}
        />
      </Space.Compact>
    );
  }

  return (
    <div
      onDoubleClick={startEdit}
      onTouchStart={onTouchStart}
      onTouchEnd={clearTimer}
      onTouchMove={clearTimer}
      onTouchCancel={clearTimer}
      style={{ padding: "2px 0" }}
    >
      <Text style={{ fontSize: 15 }}>{text}</Text>
    </div>
  );
}

/**
 * Плоская строка
 * props:
 *  - item: { _id?: string, id?: string, name: string }
 *  - onRename: (id, nextName) => Promise<boolean|void> | boolean | void
 *  - onDelete: (idOrItem) => Promise<void> | void
 */
export default function FlatRow({ item, onRename, onDelete }) {
  const [showActions, setShowActions] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const getId = (arg) =>
    typeof arg === "string" ? arg : arg?._id ?? arg?.id ?? arg?.value ?? arg?.key ?? null;

  const handleConfirmDelete = () => {
    // Передаём ID если есть, иначе весь объект — на случай, если onDelete сам нормализует
    const id = getId(item);
    onDelete?.(id ?? item);
  };

  return (
    <div
      style={{
        border: "1px solid #e8e8e8",
        borderRadius: 8,
        padding: 10,
        marginBottom: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}
      onClick={() => setShowActions((v) => !v)}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <InlineEditableText
          text={item.name}
          onRename={(val) => onRename(getId(item), val)}
          onEditingChange={setIsEditing}
        />
      </div>

      {showActions && !isEditing && (
        // Останавливаем всплытие, чтобы клик по кнопке не закрывал блок действий
        <div onClick={(e) => e.stopPropagation()}>
          <Popconfirm
            title="Видалити?"
            okText="Так"
            cancelText="Ні"
            okButtonProps={{ danger: true, size: "large" }}
            onConfirm={handleConfirmDelete}
          >
            <Tooltip title="Видалити">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined style={{ fontSize: 20 }} />}
                size="large"
                style={{ width: 40, height: 40 }}
                onClick={(e) => e.stopPropagation()} // дополнительно
              />
            </Tooltip>
          </Popconfirm>
        </div>
      )}
    </div>
  );
}
